import { api } from "../../shared/auth/auth.service";

export interface Tema {
  idTemaContenido: number;
  nombre: string;
  orden: number;
}

export interface AnioEscolar {
  idAnioEscolar: number;
  nombre: string;
  orden: number;
}

export interface Grupo {
  idGrupoContenido: number;
  nombre: string;
  orden: number;
}

export interface Unidad {
  idUnidadContenido: number;
  nombre: string;
  orden: number;
}

export interface CreateTemaDto {
  nombre: string;
  orden: number;
}

export interface CreateAnioEscolarDto {
  nombre: string;
  orden: number;
}

export interface CreateGrupoDto {
  nombre: string;
  orden: number;
}

export interface CreateUnidadDto {
  nombre: string;
  orden: number;
}

export interface BulkUploadData {
  anio: string;
  unidad: string;
  tema: string;
  objetivosAprendizaje: string;
  explicacion: string;
  ejemplo: string;
  resultado: string;
  dificultad: number;
  conceptosPrevios: string;
  tieneImagen: boolean;
}

export interface BulkUploadResponse {
  creados: {
    aniosEscolares: number;
    unidades: number;
    temas: number;
    contenidos: number;
  };
  existentes: {
    aniosEscolares: number;
    unidades: number;
    temas: number;
  };
  errores: string[];
}

class GestionCurricularService {
  // ===== TEMAS =====
  async getTemas(): Promise<Tema[]> {
    try {
      const response = await api.get("/gestion-curricular/temas");
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async crearTema(data: CreateTemaDto): Promise<Tema> {
    try {
      const response = await api.post("/gestion-curricular/temas", data);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async actualizarTema(id: number, data: CreateTemaDto): Promise<Tema> {
    try {
      const response = await api.put(`/gestion-curricular/temas/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async eliminarTema(id: number): Promise<void> {
    try {
      await api.delete(`/gestion-curricular/temas/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  // ===== AÑOS ESCOLARES =====
  async getAniosEscolares(): Promise<AnioEscolar[]> {
    try {
      const response = await api.get("/gestion-curricular/anios-escolares");
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async crearAnioEscolar(data: CreateAnioEscolarDto): Promise<AnioEscolar> {
    try {
      const response = await api.post(
        "/gestion-curricular/anios-escolares",
        data
      );
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async actualizarAnioEscolar(
    id: number,
    data: CreateAnioEscolarDto
  ): Promise<AnioEscolar> {
    try {
      const response = await api.put(
        `/gestion-curricular/anios-escolares/${id}`,
        data
      );
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async eliminarAnioEscolar(id: number): Promise<void> {
    try {
      await api.delete(`/gestion-curricular/anios-escolares/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  // ===== GRUPOS =====
  async getGrupos(): Promise<Grupo[]> {
    try {
      const response = await api.get("/gestion-curricular/grupos");
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async crearGrupo(data: CreateGrupoDto): Promise<Grupo> {
    try {
      const response = await api.post("/gestion-curricular/grupos", data);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async actualizarGrupo(id: number, data: CreateGrupoDto): Promise<Grupo> {
    try {
      const response = await api.put(`/gestion-curricular/grupos/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async eliminarGrupo(id: number): Promise<void> {
    try {
      await api.delete(`/gestion-curricular/grupos/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  // ===== UNIDADES =====
  async getUnidades(): Promise<Unidad[]> {
    try {
      const response = await api.get("/gestion-curricular/unidades");
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async crearUnidad(data: CreateUnidadDto): Promise<Unidad> {
    try {
      const response = await api.post("/gestion-curricular/unidades", data);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async actualizarUnidad(id: number, data: CreateUnidadDto): Promise<Unidad> {
    try {
      const response = await api.put(
        `/gestion-curricular/unidades/${id}`,
        data
      );
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async eliminarUnidad(id: number): Promise<void> {
    try {
      await api.delete(`/gestion-curricular/unidades/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  // ===== CARGA MASIVA =====
  async downloadTemplate(): Promise<Blob> {
    try {
      const response = await api.get(
        "/gestion-curricular/descargar-plantilla",
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async bulkUpload(data: BulkUploadData[]): Promise<BulkUploadResponse> {
    try {
      const response = await api.post("/gestion-curricular/carga-masiva", {
        datos: data,
      });
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }
}

export const gestionCurricularService = new GestionCurricularService();
